import { Button } from '@pluxee-design-system/core';
import { ProductCheckboxItem } from 'components/Products/ProductCheckbox';
import { PosTypeEnum, ProductTypeEnum } from 'generated/models';
import { I18nProps, withTranslations } from 'i18n';
import { useCallback, useMemo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import gtm from 'trackers/gtm';
import { TerminalConfig } from 'types/config';
import { CzechOnboardingValues, CzechPOSValues } from '../types';
import { getAllowedPosTypes } from '../utils';
import POSRow from './POSRow';

interface POSStepProps extends I18nProps {
  config: TerminalConfig;
  contractProducts: ProductCheckboxItem[];
  isOnboarding?: boolean;
}

const template = (id: string, allowedDevices: PosTypeEnum[]): CzechPOSValues => ({
  country: 'cz',
  id,
  bankId: '',
  deviceType: allowedDevices.find((type) => type === PosTypeEnum.Pos) ?? allowedDevices[0] ?? '',
  productIds: [],
  terminalId: '',
  terminalCode: '',
  masterId: '',
  email: '',
  certificateEmail: '',
});

const POSStep = ({ config, contractProducts = [], isOnboarding = true, t }: POSStepProps) => {
  const { append, fields, remove } = useFieldArray<CzechOnboardingValues, 'POS'>({ name: 'POS' });
  const { formState, watch } = useFormContext<CzechOnboardingValues>();
  const valuesProducts = watch('products');
  const { errors } = formState;

  const products = useMemo(
    () =>
      contractProducts.filter(
        ({ product }) =>
          product.type !== ProductTypeEnum.Paper && valuesProducts.includes(product.code),
      ),
    [contractProducts, valuesProducts],
  );
  const allowedPosTypes = useMemo(() => getAllowedPosTypes(products), [products]);

  const handleAdd = useCallback(() => {
    if (isOnboarding) gtm.trackClickLocationAddTerminal();
    append(template(fields.length.toString(), allowedPosTypes));
  }, [allowedPosTypes, append, fields.length, isOnboarding]);

  const hasPOS = fields.length > 0;
  const isValid = fields.length === 0 || (fields.length > 0 && (errors.POS?.length || 0) === 0);

  const addButton = (
    <Button
      isDisabled={!isValid}
      type="button"
      onClick={handleAdd}
      variant="secondaryOutlined"
      id="POSStep_posFormSubmitButton"
      size="md"
      alignSelf="flex-start"
    >
      {t('location_add.buttons.addTerminal', 'Add an other terminal')}
    </Button>
  );

  return (
    <>
      {fields &&
        fields.map((field, index) => (
          <POSRow
            key={`POS-field-${field.id}`}
            allowedPosTypes={allowedPosTypes}
            config={config}
            idPrefix="POSStep_"
            name={`POS.${index}.`}
            index={index}
            isMultiProductsAllowed={false}
            isOnboarding={isOnboarding}
            onRemove={remove}
            products={products}
            showDivider={index + 1 < fields.length}
          >
            {hasPOS && index === fields.length - 1 && addButton}
          </POSRow>
        ))}
      {!hasPOS && addButton}
    </>
  );
};

export default withTranslations(POSStep);
